/**
 * Turns a date into a simple "year-month-day" text string using the user's local timezone.
 * Example: July 8, 2026 at 9:28 PM Pacific becomes "2026-07-08", not a UTC timestamp.
 * User-entered calendar dates (CSLB expiration, etc.) must never be converted to UTC.
 */
export function getLocalDateString(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Converts a saved date value back into a Date the datepicker can display.
 * Strips out time-of-day so UTC storage can't shift the calendar day.
 * Returns null when nothing was saved (empty date fields).
 */
export function parseStoredDate(value: string | Date | null | undefined): Date | null {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null;
    }
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  if (value == null || value === '') {
    return null;
  }

  // Prefer calendar date from YYYY-MM-DD (also matches the date prefix of ISO strings).
  const dateOnlyMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnlyMatch) {
    return new Date(+dateOnlyMatch[1], +dateOnlyMatch[2] - 1, +dateOnlyMatch[3]);
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

/**
 * Walks form data before save and replaces every Date object with a plain local date string.
 * Prevents JSON.stringify from converting dates to UTC and shifting the day.
 */
export function serializeFormDates<T>(value: T): T {
  if (value instanceof Date) {
    return getLocalDateString(value) as T;
  }

  if (Array.isArray(value)) {
    return value.map(item => serializeFormDates(item)) as T;
  }

  if (value !== null && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    Object.keys(value as object).forEach(key => {
      result[key] = serializeFormDates((value as Record<string, unknown>)[key]);
    });
    return result as T;
  }

  return value;
}
